import { TWidget } from 'widgets/Widget';
import { RefElement, TRefElementInstance } from 'widgets/toolbox/RefElement';

/**
 * @description Base ProductVariantSelect implementation
 * @param Widget Base widget for extending
 * @returns Product Variant Select widget
 */
function ProductVariantSelectClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class ProductVariantSelect
     * @augments Widget
     *
     * @example <caption>Product Variant Select widget usage</caption>
     * <section
     *     class="b-variations_item m-swatch m-${attr.id}"
     *     aria-label="${attr.displayName}"
     *     data-widget="productVariantSelect"
     *     data-tau="product_variations_${attr.id}"
     *     data-widget-event-change="changeAttribute"
     *     data-id="attr-${attr.id}"
     *     data-skip-validation="true"
     * >
     *     <div data-ref="container">
     *         <h2 class="b-variations_item-label">${attr.displayName}:</h2>
     *
     *         <div class="b-variations_item-empty_value">
     *             ${Resource.msg('form.resource.emptyoption', 'forms', null)}
     *         </div>
     *         <div
     *             role="radiogroup"
     *             aria-label="${attr.displayName}"
     *             aria-describedby="variation-error-${productIndex}-${attr.id}"
     *             class="b-variations_item-content m-list"
     *         >
     *             ... content
     *         </div>
     *         <div
     *             role="alert"
     *             id="variation-error-${productIndex}-${attr.id}"
     *             class="b-variations_item-error"
     *             data-ref="errorFeedback"
     *             hidden="hidden"
     *         ></div>
     *     </div>
     *
     *     <script type="template/mustache" data-ref="template">
     *         <div data-ref="container">
     *             ...
     *             <div
     *                 role="radiogroup"
     *                 aria-labelledby="variation-label-${attr.id} variation-error-${productIndex}-${attr.id}"
     *                 class="b-variations_item-content m-list"
     *             >
     *                 ... dynamic product variant select content
     *             </div>
     *             ...
     *         </div>
     *     </script>
     * </section>
     */
    class ProductVariantSelect extends Widget {
        selectedVariation: null | TRefElementInstance = null;

        /**
         * @description saves selected variation to local field
         * @param variation - selected variation
         * @emits 'change'
         */
        selectVariation(variation: TRefElementInstance) {
            this.selectedVariation = variation;
            this.emit('change', this);
        }

        /**
         * @description gets selected variation element
         * @returns selected variation or ref element of empty array
         */
        getSelectedVariations(): TRefElementInstance {
            if (this.selectedVariation) {
                return this.selectedVariation;
            }

            return new RefElement([]);
        }
    }

    return ProductVariantSelect;
}

export type TProductVariantSelect = ReturnType<typeof ProductVariantSelectClassCreator>;

export type TProductVariantSelectInstance = InstanceType<TProductVariantSelect>;

export default ProductVariantSelectClassCreator;
